import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : column type definitions
// =============================================================================
// ---Third-party imports
import React, { createContext, useContext } from 'react';
import { Image, Icon, Button } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { get, dict, keys, values, items, isNil, sum, size, countBy, mapItems, uniq, isDigit, strip, isArray, isString, isSet, sorted, flatten, isFunction } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import { Link, UnknownDomainLink } from 'uix/components';
import { useSelector, useState, useDebounce, useEffect, useInView } from 'uix/hooks';
import log from 'uix/logging';
import { hashObj, prettyBytes, ucFirst, formatDate, formatSI, arrayMax, arrayMin, formatDateTime } from 'uix/utils';
import { useStatusType } from 'uix/states/hooks';
import { STATE_NAME2VALUE } from 'uix/states/constants';
import { StateIcon } from 'uix/states/components';
import { Graphics, VIcon } from 'uix/graphics';
import { UserInfo } from 'uix/auth/components';
import { copyButton } from 'uix/components/table';
import { filterNumbers, filterMatchSorter, filterOptions, filterDates, constructOptionFilterValue, MultiChoicePopup } from 'uix/components/table/cols';
import tableStyles from 'uix/components/table/styles';
import { DomainIcon } from 'uix/domain/icon';
import { TypeIcon } from 'uix/types/components';
// ---Local imports
import styles from './styles.scss';
// ---Globals
const SIGMA = String.fromCodePoint(0x03a3);
const O_STROKE = String.fromCodePoint(0x2205);
export const ColumnContext = /*#__PURE__*/ createContext({});
export const useColumnContext = ()=>useContext(ColumnContext);
const filterSortNumbers = (opts = {})=>{
    /* add sorting aspect for grouped columns
  to numerical columns
  */ let colcfg = filterNumbers(opts);
    let sortMethod = (a, b)=>{
        if (isArray(a)) {
            // see NumberColumn aggregate
            a = a[0][1];
        }
        if (isArray(b)) {
            // see NumberColumn aggregate
            b = b[0][1];
        }
        // move null + undefined to the bottom
        if (isNil(a)) {
            a = -Infinity;
        }
        if (isNil(b)) {
            b = -Infinity;
        }
        return a < b ? -1 : a > b ? 1 : 0;
    };
    return {
        ...colcfg,
        sortMethod
    };
};
export const getColumnNamespaceLabel = (ns, i18n, labelFunc, as_span = true)=>{
    let k = `colns_${ns}`;
    let label = as_span ? i18n.span(`colns_${ns}`) : i18n.get(`colns_${ns}`);
    return labelFunc ? labelFunc(ns) || label : label;
};
export const getColumLabel = (colid, i18n, i18nKey, labelFunc, as_span = true)=>{
    let label = as_span ? i18n.span(i18nKey || `col_${colid}`) : i18n.get(i18nKey || `col_${colid}`);
    return labelFunc ? labelFunc(colid) || label : label;
};
export const ColHeader = ({ i18nKey, colid, namespace = null })=>{
    let i18n = useSelector(Sel.i18n);
    let { columnLabel, columnNamespaceLabel } = useColumnContext();
    let res = getColumLabel(colid, i18n, i18nKey, columnLabel, true);
    if (namespace) {
        res = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                getColumnNamespaceLabel(namespace, i18n, columnNamespaceLabel),
                ': ',
                res
            ]
        });
    }
    return res;
};
const typeIcon = (name, title)=>/*#__PURE__*/ _jsx(TypeIcon, {
        name: name,
        title: title
    });
const col2field = (colid)=>colid.replace('.', '_');
export const nodeAccessor = (colid, f)=>{
    let field = col2field(colid);
    if (!f) {
        return field //node => node[field]
        ;
    } else {
        return (node)=>f(node, field);
    }
};
const renderSumAvg = (count, sum, formatter, onlyAvg)=>/*#__PURE__*/ _jsxs("span", {
        style: {
            display: 'flex'
        },
        children: [
            !onlyAvg && sum > 0 && /*#__PURE__*/ _jsxs("span", {
                style: {
                    flex: 1
                },
                children: [
                    /*#__PURE__*/ _jsx("b", {
                        children: SIGMA
                    }),
                    " ",
                    formatter(sum)
                ]
            }),
            sum > 0 && count > 0 && /*#__PURE__*/ _jsxs("span", {
                style: {
                    flex: 1
                },
                children: [
                    /*#__PURE__*/ _jsx("b", {
                        children: O_STROKE
                    }),
                    " ",
                    formatter(sum / count)
                ]
            })
        ]
    });
const FileSizeColumn = (env, colid, options)=>({
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return renderSumAvg(value[0], value[1], prettyBytes);
            }
            return value ? prettyBytes(value) : null;
        },
        AggregatedFooter: ([count, sum_vals])=>renderSumAvg(count, sum_vals, prettyBytes),
        aggregate: (vals, data, is_aggr)=>{
            if (is_aggr) {
                return [
                    size(vals),
                    sum(vals.map((e)=>e[1]))
                ];
            }
            let filtered = vals.filter((e)=>e);
            return [
                size(filtered),
                sum(filtered)
            ];
        },
        ...filterSortNumbers(),
        ...options
    });
let mimetype2id = {
    'image/tiff': 1,
    'image/png': 2,
    'image/jpeg': 3,
    'image/jp2': 4,
    'image/webp': 5,
    'image/bpg': 6,
    'image/jxr': 7
};
mimetype2id = {
    ...mimetype2id,
    ...dict(mapItems(mimetype2id, (k, v)=>[
            v,
            k
        ]))
};
const MimetypeColumn = (env, colid, options)=>({
        /* translates mimetype integers (from OPR_Page) to strings
   */ Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return value.map((v)=>mimetype2id[v]).join(' ');
            } else {
                return value ? mimetype2id[value] || 'unknown' : null;
            }
        },
        aggregate: (vals)=>uniq(vals),
        PivotValue: ({ value, subRows })=>{
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    mimetype2id[value],
                    ` (${size(subRows)})`
                ]
            });
        },
        ...options
    });
const MimetypeTextColumn = (env, colid, options)=>{
    /* emits mimetypes
   */ // text/markdown -> MARKDOWN
    // application/xslt+xml -> XSLT
    let render_val = (v)=>v?.split('/').slice(-1)[0].split('+')[0].toUpperCase() || '';
    return {
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return value.map((v)=>render_val(v)).join(' ');
            } else {
                return render_val(value) || null;
            }
        },
        aggregate: (vals)=>uniq(vals),
        PivotValue: ({ value, subRows })=>{
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    render_val(value),
                    ` (${size(subRows)})`
                ]
            });
        },
        groupBySupport: true,
        ...options
    };
};
const MetadataRelationColumn = (env, colid)=>{
    let { i18n } = env;
    // metadata.* -> metadata
    let field_prefix = colid.slice(0, -2);
    let id_field = `${field_prefix}_id`;
    let st_field = `${field_prefix}_rcv_type`;
    let caption_field = `${field_prefix}_caption`;
    return {
        Header: ()=>/*#__PURE__*/ _jsx(ColHeader, {
                colid: colid
            }),
        accessor: (node)=>node[id_field],
        Cell: ({ value, aggregated, original })=>{
            if (aggregated) {
                let [valueAggregated, uniqueAggregated] = value;
                if (uniqueAggregated > 0) {
                    return copyButton(valueAggregated, String(uniqueAggregated), i18n.get('cell_copy_ids', {
                        count: uniqueAggregated
                    }));
                }
                return null;
            } else if (value) {
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        typeIcon(`1_${original[st_field]}`),
                        " ",
                        original[caption_field]
                    ]
                });
            }
            return null;
        },
        aggregate: (vals)=>{
            let u = uniq(vals.filter((e)=>e).map((e)=>e.id));
            return [
                u.join(', '),
                u.length
            ];
        },
        PivotValue: ({ value, subRows, ...r })=>{
            let span = ()=>{
                // fetch data from first subrow
                let original = subRows[0]._original;
                let [id, caption, rcv_type] = [
                    'id',
                    'caption',
                    'rcv_type'
                ].map((n)=>original[`${field_prefix}_${n}`]);
                if (!caption) {
                    caption = '';
                }
                return /*#__PURE__*/ _jsxs("span", {
                    title: `${caption} [${value}]`,
                    children: [
                        typeIcon(`1_${rcv_type}`),
                        ' ',
                        caption.slice(0, 20) + (caption.length > 20 ? '... ' : ' ')
                    ]
                });
            };
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    value !== 'undefined' ? span() : i18n.get('pivot_no_value'),
                    ` (${size(subRows)})`
                ]
            });
        },
        groupBySupport: true,
        serializeCSV: (value, row)=>row._original[caption_field],
        ...filterMatchSorter(`_original.${field_prefix}_caption`)
    };
};
const DateColumn = (env, colid, options)=>{
    let { format = 'ISO', field } = options // 'fullDate'
    ;
    field = field ? col2field(field) : field;
    let _date = (v)=>new Date(v);
    let HeaderButtons = null;
    if (colid == env.groupByValue) {
        _date = (v)=>{
            let d = new Date(v);
            d.setMinutes(0);
            d.setHours(0);
            d.setSeconds(0);
            if (env.groupedDateGranularity == 'm') {
                d.setDate(1);
            }
            if (env.groupedDateGranularity == 'y') {
                d.setMonth(0);
                d.setDate(1);
            }
            return d;
        };
        format = 'yyyy-LL-dd';
        if (env.groupedDateGranularity == 'm') {
            format = 'yyyy-LL';
        }
        if (env.groupedDateGranularity == 'y') {
            format = 'yyyy';
        }
        let onClick = (v)=>(event)=>{
                env.setGroupedDateGranularity(v);
                event.stopPropagation();
            };
        let button = (v)=>/*#__PURE__*/ _jsx(Button, {
                style: {
                    padding: '0.2em',
                    zIndex: 100
                },
                basic: true,
                compact: true,
                color: "grey",
                size: "tiny",
                active: v == env.groupedDateGranularity,
                content: v.toUpperCase(),
                floated: "right",
                onClick: onClick(v)
            });
        HeaderButtons = /*#__PURE__*/ _jsxs(Button.Group, {
            floated: "left",
            style: {
                paddingLeft: '0.2em'
            },
            children: [
                button('d'),
                button('m'),
                button('y')
            ]
        });
    }
    //~ if (env.groupByValue) log.x(colid, 'XXX', env.groupByValue)
    let aggregatedDates = (value)=>{
        let [dateMin, dateMax] = value;
        if (!dateMin) {
            return null;
        }
        if (dateMin == dateMax) {
            return `${formatDate(dateMin, format)}`;
        }
        //~ let slimformat = 'yyyy-LL'
        for (let slimformat of [
            'yyyy',
            'yyyy-LL',
            'yyyy-LL-dd'
        ]){
            let [_from, _until] = [
                formatDate(dateMin, slimformat),
                formatDate(dateMax, slimformat)
            ];
            if (_from != _until || slimformat == 'yyyy-LL-dd') {
                if (_from == _until) {
                    // same day
                    return _from;
                }
                return `${_from}\u27f6${_until}`;
            }
        }
    };
    return {
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return aggregatedDates(value);
            } else {
                return value ? formatDate(value, format) : null;
            }
        },
        accessor: nodeAccessor(colid, (node, colid_field)=>{
            let v = node[field || colid_field];
            return isNil(v) ? null : _date(v);
        }),
        AggregatedFooter: (aggregated)=>aggregatedDates(aggregated),
        aggregate: (vals, data, isPreAggregated)=>{
            if (isPreAggregated) {
                vals = flatten(vals);
            }
            let v = vals.filter((d)=>d && (Number.isFinite(d) || typeof d == 'object'));
            if (v.length > 0) {
                return [
                    arrayMin(v),
                    arrayMax(v)
                ];
            }
            return [
                null,
                null
            ];
        },
        PivotValue: ({ value, subRows })=>{
            // XXX: value (_pivotVal) is converted to "Fri Apr 16 2021 02:00:00 GMT+0200 (Mitteleuropäische Sommerzeit)"
            // therefore: new Date(value)
            return `${value !== 'null' ? formatDate(new Date(value), format) : 'Leer'} (${size(subRows)})`;
        },
        serializeCSV: (v)=>{
            if (isArray(v)) {
                let d1 = formatDate(v[0], format);
                let d2 = formatDate(v[1], format);
                if (d1 == d2) {
                    return d1;
                } else {
                    return `${d1} - ${d2}`;
                }
            }
            return formatDate(v, format);
        },
        groupBySupport: true,
        HeaderButtons,
        ...filterDates(colid),
        ...options
    };
};
const boolFilterOptions = (i18n)=>[
        'true',
        'false'
    ].map((n)=>({
            value: n,
            label: i18n.get(n == 'true' ? 'yes' : 'no')
        }));
const BooleanColumn = (env, colid, options)=>{
    let trueVal = options.trueVal === undefined ? true : options.trueVal;
    let { mapper, field } = options;
    field = field ? col2field(field) : field;
    let icon_true = options.icon || 'check';
    let icon_false = 'minus';
    let check_count = (value)=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                value[0] > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            name: icon_true,
                            color: "green"
                        }),
                        String(value[0])
                    ]
                }),
                value[1] > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            name: icon_false,
                            color: 'red'
                        }),
                        String(value[1])
                    ]
                })
            ]
        });
    return {
        accessor: nodeAccessor(colid, (node, colid_field)=>{
            let value = node[field || colid_field];
            if (value !== undefined) {
                return isNil(value) ? null : (mapper ? mapper(value) : value) === trueVal;
            } // else return undefined during loading
        }),
        Cell: ({ value, aggregated })=>{
            if (aggregated && value) {
                return check_count(value);
            }
            if (value === true) {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: icon_true,
                    color: "green"
                });
            } else if (value === false) {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: icon_false,
                    color: "red"
                });
            }
            return null;
        },
        AggregatedFooter: (aggregated)=>check_count(aggregated),
        aggregate: (vals, data, isPreAggregated)=>{
            if (isPreAggregated) {
                let count_true = 0;
                let count_false = 0;
                vals.forEach((e)=>{
                    count_true += e[0];
                    count_false += e[1];
                });
                return [
                    count_true,
                    count_false
                ];
            }
            let count_true = 0;
            let count_false = 0;
            vals.forEach((e)=>{
                if (e === true) {
                    count_true += 1;
                } else if (e === false) {
                    count_false += 1;
                } // else not data
            });
            return [
                count_true,
                count_false
            ];
        },
        style: {
            textAlign: 'center'
        },
        PivotValue: ({ value, subRows })=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        color: {
                            true: 'green',
                            false: 'red'
                        }[value],
                        name: {
                            true: icon_true,
                            false: icon_false,
                            null: 'exclamation triangle'
                        }[value] || 'question'
                    }),
                    ` (${size(subRows)})`
                ]
            }),
        groupBySupport: true,
        ...filterOptions(boolFilterOptions(env.i18n)),
        ...options
    };
};
// interpret all ints > 0 as true, else false
// prefer first element of custom fields array
const CustomBooleanColumn = (env, colid, options)=>{
    let f = col2field(options.fields?.[0] || colid);
    return BooleanColumn(env, colid, {
        ...options ?? {},
        accessor: (node)=>{
            let v = node[f];
            // return undefined during loading (undefined will always be filtered in filterMethod())
            return v !== undefined ? !!v : undefined;
        }
    });
};
const CategorialColumn = (env, colid, options)=>{
    let labels = options.labels ?? {};
    if (isFunction(labels)) {
        labels = labels(env);
    }
    return {
        Cell: ({ value, aggregated })=>{
            if (aggregated && value) {
                let values = sorted(mapItems(value, (k, v)=>[
                        k,
                        v
                    ]), (e)=>e[1], true);
                values = values.slice(0, 5);
                return values.filter(([v])=>v !== 'null') // no unresolved puids in aggregation
                .map(([v, c])=>// return empty string during load (=== undefined)
                    /*#__PURE__*/ _jsx("span", {
                        title: c,
                        children: `${labels[v] || '?'}: ${formatSI(c)} `
                    }, v));
            }
            return value === undefined ? '' : labels[value] || '?';
        },
        aggregate: (vals)=>countBy(vals),
        PivotValue: ({ value, subRows })=>{
            return `${value === 'undefined' ? '' : labels[value] || '?'} (${size(subRows)})`;
        },
        groupBySupport: true,
        ...filterOptions(mapItems(labels, (value, label)=>({
                value,
                label
            }))),
        ...options
    };
};
const TextColumn = (env, colid, options)=>{
    /* options:
    copyButton: show copy button in aggregation cell
    uniquify: aggregate unique values
  */ let { i18n } = env;
    let { translate, copySeparator = ', ' } = options;
    let col = {
        Footer: ({ data, column })=>{
            let numAggregated = 0;
            let content = '';
            if (data[0]?._aggregated) {
                let non_empty = data.map((e)=>e[column.id][0]).filter((e)=>e[1] > 0);
                content = non_empty.map((e)=>e[0]).join(copySeparator);
                numAggregated = sum(non_empty.map((e)=>e[1]));
            } else {
                let non_empty = data.map((e)=>e[column.id]).filter((e)=>e);
                numAggregated = non_empty.length;
                content = non_empty.join(copySeparator);
            }
            return copyButton(content, String(numAggregated), i18n.get('cell_copy', {
                count: numAggregated
            }));
        },
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                let [valueAggregated, numfilteredAggregated] = value;
                if (options.copyButton && numfilteredAggregated > 0) {
                    return copyButton(valueAggregated, String(numfilteredAggregated), i18n.get('cell_copy', {
                        count: numfilteredAggregated
                    }));
                }
                if (options.groupBySupport) {
                    let v = valueAggregated.split(copySeparator).map((v)=>translate ? translate(strip(v), i18n) : strip(v));
                    return v.join(' ');
                }
                return null;
            } else {
                if (translate) {
                    return translate(value, i18n);
                }
                return value || null;
            }
        },
        aggregate: (vals)=>{
            let v = vals.filter((e)=>e);
            if (options.uniquify) {
                v = uniq(v);
            }
            return [
                v.join(copySeparator),
                v.length
            ];
        },
        sortMethod: (a, b)=>{
            if (isArray(a)) {
                // aggregated case (see v.length above)
                a = a[0][1];
            }
            if (isArray(b)) {
                // aggregated case (see v.length above)
                b = b[0][1];
            }
            // move null + undefined to the bottom
            if (isNil(a)) {
                a = '';
            }
            if (isNil(b)) {
                b = '';
            }
            if (isString(a) && isString(b)) {
                return a.localeCompare(b);
            } else {
                return a < b ? -1 : a > b ? 1 : 0;
            }
        }
    };
    if (options.groupBySupport) {
        col = {
            ...col,
            groupBySupport: true,
            PivotValue: ({ value, subRows })=>{
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        translate ? translate(value, i18n) : value,
                        ` (${size(subRows)})`
                    ]
                });
            }
        };
    }
    return {
        ...col,
        ...filterMatchSorter(colid),
        ...options
    };
};
const RESTYPE2URL = {
    pdf: '/download/pdf/',
    fpk_plain: '/download/ftpack/plain/',
    fpk_alto: '/download/ftpack/alto/',
    fpk_html: '/download/ftpack/html/',
    wc0: '/download/webcache/0/',
    archive: '/download/archive/'
};
const ResourceLinkColumn = (env, colid, options)=>{
    let { i18n, apiurl } = env;
    let { restype, renderCell } = options;
    // field/fields should contain OR_ID field
    let id_field = col2field(`${restype}.id`);
    let urlpath = RESTYPE2URL[restype];
    let accessor = (node)=>node[id_field] ? apiurl.baseUrl(`/download/resource/${node[id_field]}`) : null;
    if (urlpath) {
        accessor = (node)=>node[id_field] ? apiurl.baseUrl(urlpath + node.otid) : null;
    }
    options = {
        ...options,
        copyButton: true,
        copySeparator: '\n'
    };
    return {
        ...TextColumn(env, colid, options),
        Cell: ({ value, aggregated, original })=>{
            if (aggregated) {
                // like in TextColumn
                let [valueAggregated, numfilteredAggregated] = value;
                if (options.copyButton && numfilteredAggregated > 0) {
                    return copyButton(valueAggregated, String(numfilteredAggregated), i18n.get('cell_copy', {
                        count: numfilteredAggregated
                    }));
                }
                return null;
            }
            if (renderCell) {
                return renderCell(value, original);
            } else if (value) {
                return /*#__PURE__*/ _jsx("a", {
                    href: value,
                    children: value
                });
            }
            return null;
        },
        accessor
    };
};
const ResourceColumn = (env, colid, options)=>{
    let { restype } = options;
    let id_field = col2field(`${restype}.id`);
    let mts_field = col2field(`${restype}.modificationtimestamp`);
    let fs_field = col2field(`${restype}.filesize`);
    let fn_field = col2field(`${restype}.filename`);
    let renderCell = (href, original)=>{
        let otid = original.otid;
        let numbytes = original[fs_field];
        let filename = original[fn_field];
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        float: 'right'
                    },
                    children: formatDateTime(original[mts_field])
                }),
                /*#__PURE__*/ _jsx("a", {
                    title: filename,
                    href: href,
                    download: true,
                    children: prettyBytes(numbytes || 0)
                }),
                filename && restype == 'genres' ? ` (${filename.split('.').at(-1)})` : null
            ]
        });
    };
    options = {
        ...options,
        renderCell
    };
    return {
        ...ResourceLinkColumn(env, colid, options)
    };
};
const NumberColumn = (env, colid, options)=>{
    let accessor = nodeAccessor(colid);
    if (options.transform) {
        let f = options.transform;
        accessor = nodeAccessor(colid, (node, field)=>f(node[field]));
    }
    let fmt = (v)=>formatSI(v, 1);
    return {
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return renderSumAvg(value[0], value[1], fmt);
            }
            return value ?? null;
        },
        aggregate: (vals, data, is_aggr)=>{
            if (is_aggr) {
                return [
                    size(vals),
                    sum(vals.map((e)=>e[1]))
                ];
            }
            return [
                size(vals),
                sum(vals)
            ];
        },
        AggregatedFooter: ([count, sum_vals])=>renderSumAvg(count, sum_vals, fmt),
        // use sum() part of tuple if aggregated
        serializeCSV: (v)=>isArray(v) ? v[1] : v,
        accessor,
        ...filterSortNumbers(),
        ...options
    };
};
const PercentageColumn = (env, colid, options)=>{
    let accessor = nodeAccessor(colid);
    let f = options.factor || 1;
    let fmt = (value)=>value ? `${value.toFixed(1)} %` : null;
    if (options.inverted) {
        accessor = nodeAccessor(colid, (node, field)=>isNil(node[field]) ? null : 100 - node[field] * f);
    } else if (f > 1) {
        accessor = nodeAccessor(colid, (node, field)=>node[field] * f || 0);
    }
    let col = {
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                // percentage can only be averaged (onlyAvg=true)
                return renderSumAvg(value[0], value[1], fmt, true);
            }
            return fmt(value);
        },
        aggregate: (vals, data, is_aggr)=>{
            if (is_aggr) {
                return [
                    size(vals),
                    sum(vals.map((e)=>e[1]))
                ];
            }
            vals = vals.filter((v)=>v !== null);
            return [
                size(vals),
                sum(vals)
            ];
        },
        AggregatedFooter: ([count, sum_vals])=>renderSumAvg(count, sum_vals, fmt, true),
        accessor,
        ...filterSortNumbers(),
        ...options
    };
    return col;
};
const ApiThumb = ({ otid, odid, link = false })=>{
    let apiurl = useSelector(Sel.apiurl);
    // use loading="lazy"? ios-compat?
    let [ref, onScreen] = useInView({
        triggerOnce: true
    });
    let res = /*#__PURE__*/ _jsx("img", {
        ref: ref,
        src: onScreen ? `${apiurl}/download/webcache/128/${otid}` : undefined,
        style: {
            verticalAlign: 'middle',
            width: '12px',
            height: '15px'
        }
    });
    if (link) {
        res = /*#__PURE__*/ _jsx(Link, {
            id: otid,
            odid: odid,
            lazy: true,
            children: res
        });
    }
    return res;
};
const PageImageColumn = (env, colid, options)=>{
    return {
        Cell: ({ value, aggregated, original })=>{
            if (aggregated) {
                return value.slice(0, 8).map((otid, i)=>/*#__PURE__*/ _jsx(ApiThumb, {
                        otid: otid
                    }, i));
            }
            return value ? /*#__PURE__*/ _jsx(ApiThumb, {
                otid: value,
                odid: original.odid,
                link: true
            }) : null;
        },
        PivotValue: ({ value, subRows })=>{
            return /*#__PURE__*/ _jsx(_Fragment, {
                children: ` (${size(subRows)})`
            });
        },
        aggregate: (vals)=>vals.filter((e)=>e),
        style: {
            textAlign: 'center'
        },
        filterable: false,
        width: 80,
        ...options
    };
};
const StatusCell = ({ statusType, statusValue, i18n })=>{
    let { states, byValue } = useStatusType(statusType);
    if (!isNil(statusValue)) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                states && /*#__PURE__*/ _jsx(StateIcon, {
                    type: statusType,
                    name: get(byValue[statusValue], 'name'),
                    width: 16,
                    verticalAlign: "sub"
                }),
                ' ',
                i18n.span(`${statusType}_state_${statusValue}`)
            ]
        });
    } else {
        return null;
    }
};
const StatusAggregated = ({ statusType, valueCounts })=>{
    let i18n = useSelector(Sel.i18n);
    let { states, byValue } = useStatusType(statusType);
    if (!states) {
        return null;
    }
    let iconCounts = mapItems(valueCounts, (value, count)=>{
        let translated = i18n.get(`${statusType}_state_${value}`);
        return /*#__PURE__*/ _jsx(StateIcon, {
            type: statusType,
            name: get(byValue, value, {}).name,
            width: 16,
            title: `${translated} (${count})`,
            verticalAlign: "sub"
        }, value);
    }, (value)=>value !== 'undefined');
    return iconCounts;
};
const StatusFilter = ({ statusType, filter, onChange, i18n })=>{
    let { states } = useStatusType(statusType) || {};
    let value = 'all';
    let custom_opt = null;
    if (isString(filter?.value)) {
        value = filter.value;
    } else if (isSet(filter?.value) && size(filter.value)) {
        let values = [
            ...filter.value
        ];
        value = values.join(',');
        let count_more = null;
        if (values.length > 3) {
            count_more = values.length - 3;
            values = values.slice(0, 3);
        }
        custom_opt = /*#__PURE__*/ _jsxs("option", {
            value: value,
            children: [
                values.map((v)=>i18n.get(`${statusType}_state_${v}`)).join(','),
                count_more && ` [${count_more} mehr]`
            ]
        }, "custom");
    }
    let options = values(states).map((e)=>({
            value: e.value,
            label: i18n.get(`${statusType}_state_${e.value}`)
        }));
    return /*#__PURE__*/ _jsxs("span", {
        style: {
            width: '100%',
            display: 'flex'
        },
        children: [
            /*#__PURE__*/ _jsxs("select", {
                onChange: (event)=>onChange(event.target.value),
                style: {
                    flex: 1
                },
                value: value,
                className: clsx(tableStyles.colFilter, value !== 'all' && tableStyles.activeFilter),
                children: [
                    custom_opt,
                    /*#__PURE__*/ _jsx("option", {
                        value: "all",
                        children: "Alle"
                    }, "all"),
                    options.map((o)=>/*#__PURE__*/ _jsx("option", {
                            value: String(o.value),
                            children: o.label
                        }, o.value))
                ]
            }),
            /*#__PURE__*/ _jsx(MultiChoicePopup, {
                options: options,
                values: value.split(','),
                setValues: (values)=>onChange(new Set(values))
            })
        ]
    });
};
// reuse release state rendering for od.state
const DomainStateColumn = (env, colid, options)=>({
        Cell: ({ value, aggregated })=>!aggregated ? /*#__PURE__*/ _jsx(StatusCell, {
                statusType: "release",
                statusValue: value,
                i18n: env.i18n
            }) : null
    });
const StateColumn = (env, colid, options)=>{
    let { statusType, i18nKey } = options;
    let { i18n } = env;
    return {
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return /*#__PURE__*/ _jsx(StatusAggregated, {
                    statusType: statusType,
                    valueCounts: value
                });
            } else {
                return /*#__PURE__*/ _jsx(StatusCell, {
                    statusType: statusType,
                    statusValue: value,
                    i18n: env.i18n
                });
            }
        },
        PivotValue: ({ value, subRows })=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    value !== 'null' && value !== 'undefined' && /*#__PURE__*/ _jsx(StatusCell, {
                        statusType: statusType,
                        statusValue: value,
                        i18n: env.i18n
                    }),
                    value === 'null' && 'Leer',
                    ` (${size(subRows)})`
                ]
            }),
        filterMethod: (filter, row)=>{
            if (row._aggregated || filter.value === 'all') {
                return true;
            }
            let row_val = String(row[filter.id]);
            if (isSet(filter.value)) {
                // or relation
                return filter.value.has(row_val);
            } else {
                return row_val === filter.value;
            }
        },
        Filter: ({ filter, onChange })=>/*#__PURE__*/ _jsx(StatusFilter, {
                statusType: statusType,
                filter: filter,
                onChange: onChange,
                i18n: env.i18n
            }),
        constructFilter: constructOptionFilterValue(STATE_NAME2VALUE[statusType] || {}),
        aggregate: (vals)=>countBy(vals),
        groupBySupport: true,
        i18nKey,
        serializeCSV: (value)=>env.i18n.get(`${statusType}_state_${value}`)
    };
};
let buildMultiUser = (value)=>{
    if (isArray(value)) {
        return value.map((puid)=>/*#__PURE__*/ _jsx(UserInfo, {
                puid: puid
            }, puid));
    }
    if (!value) {
        return null;
    }
    return /*#__PURE__*/ _jsx(UserInfo, {
        puid: value
    });
};
const UserColumn = (env, colid, options)=>({
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                let values = sorted(mapItems(value, (k, v)=>[
                        k,
                        v
                    ]), (e)=>e[1], true);
                values = values.slice(0, 5);
                let shorten = values.length > 2;
                return values.filter(([puid, count])=>puid !== 'null') // no unresolved puids in aggregation
                .map(([puid, count])=>/*#__PURE__*/ _jsx(UserInfo, {
                        puid: puid,
                        shortenInitials: shorten,
                        title: `${count} items`
                    }, puid));
            }
            return buildMultiUser(value);
        },
        //~ filterable: false,
        filterMethod: (filter, row)=>{
            if (row._aggregated || !filter.value) {
                return true;
            }
            let row_val = row[filter.id];
            if (isArray(row_val)) {
                return !!row_val.find((e)=>e == filter.value);
            } else if (row_val == filter.value) {
                return true;
            }
            return false;
        },
        aggregate: (vals)=>countBy(vals),
        groupBySupport: true,
        PivotValue: ({ value, subRows })=>// buildMultiUser seems not to work here (checkpoint_puids)
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    buildMultiUser(value),
                    ` (${size(subRows)})`
                ]
            }),
        ...options
    });
const copyButtonIDs = (env, valueAggregated, numAggregated)=>copyButton(valueAggregated, String(numAggregated), env.i18n.get('cell_copy_ids', {
        count: numAggregated
    }));
const IdColumn = (env, colid, options)=>({
        Header: ()=>'VLID',
        AggregatedFooter: ([id_list, num_ids])=>copyButtonIDs(env, id_list, num_ids),
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                let [valueAggregated, numAggregated] = value;
                return copyButtonIDs(env, valueAggregated, numAggregated);
            } else {
                return value;
            }
        },
        aggregate: (vals, data, isPreAggregated)=>{
            if (isPreAggregated) {
                return [
                    vals.map((e)=>e[0]).filter((e)=>e).join(', '),
                    sum(vals.map((e)=>e[1]))
                ];
            }
            return [
                vals.join(', '),
                vals.length
            ];
        },
        needsColData: false,
        ...options
    });
const RelatedNode = ({ otid, itypes, buildGo, i18n })=>{
    // not guaranteed, that we will find node data for this id. But it's probable.
    let node = useSelector((root)=>root.data.tree.byId[otid]);
    if (node) {
        return buildTypeCaption(node, itypes, buildGo, i18n);
    }
    return /*#__PURE__*/ _jsx(UnknownDomainLink, {
        id: otid,
        children: otid
    });
};
const RelatedIdColumn = (env, colid, options)=>{
    let { itypes, buildGo, i18n } = env;
    return {
        Header: ()=>/*#__PURE__*/ _jsx(ColHeader, {
                colid: colid
            }),
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                let [valueAggregated, numAggregated] = value;
                return copyButton(valueAggregated, String(numAggregated), env.i18n.get('cell_copy_ids', {
                    count: numAggregated
                }));
            } else if (value) {
                return /*#__PURE__*/ _jsx(RelatedNode, {
                    otid: value,
                    itypes: itypes,
                    buildGo: buildGo,
                    i18n: i18n
                });
            }
            return null;
        },
        aggregate: (vals)=>((v)=>[
                    v.join(', '),
                    v.length
                ])(uniq(vals).filter((e)=>e)),
        ...options
    };
};
const DomainColumn = (env, colid, options = {})=>{
    let { domains, rowIndex = {
        odids: []
    } } = env;
    let domainFilterOptions = rowIndex.odids.filter((odid)=>domains.odid2name[odid]).map((odid)=>({
            value: domains.odid2name[odid],
            label: domains.data[domains.odid2name[odid]].description
        }));
    let renderLabel = options.renderLabel || ((ddata)=>ddata.description);
    return {
        Header: /*#__PURE__*/ _jsx(ColHeader, {
            colid: colid
        }),
        accessor: (node)=>domains.odid2name[node.odlink || node.odid],
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return mapItems(value, (k, v)=>/*#__PURE__*/ _jsx("span", {
                        title: `${domains.data[k].description} (${v})`,
                        children: /*#__PURE__*/ _jsx(DomainIcon, {
                            dname: domains.data[k].name
                        })
                    }, k), (k)=>domains.data[k]);
            }
            return /*#__PURE__*/ _jsxs("span", {
                title: domains.data[value].description,
                children: [
                    /*#__PURE__*/ _jsx(DomainIcon, {
                        dname: domains.data[value].name
                    }),
                    renderLabel(domains.data[value])
                ]
            });
        },
        PivotValue: ({ value, subRows })=>{
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(DomainIcon, {
                        dname: domains.data[value].name
                    }),
                    domains.data[value].description,
                    ` (${size(subRows)})`
                ]
            });
        },
        aggregate: (vals)=>countBy(vals),
        ...filterOptions(domainFilterOptions),
        ...options,
        needsColData: false
    };
};
const DomainPropertyColumn = (domprop, i18nKey, dpropopts = {})=>(env, colid, options)=>{
        /* fetch data directly from available domain props
   */ let { domains, rowIndex = {
            odids: []
        }, i18n } = env;
        let { translate } = dpropopts;
        let get_label = (v)=>{
            if (translate) {
                return translate(v, i18n);
            }
            return v;
        };
        let odid2ddata = dict(mapItems(domains.data, (dname, ddata)=>[
                ddata.id,
                ddata
            ]));
        let filterConfig = {};
        if (dpropopts.isCategorial) {
            let domainFilterOptions = uniq(rowIndex.odids.filter((odid)=>domains.odid2name[odid]).map((odid)=>domains.data[domains.odid2name[odid]][domprop])).map((value)=>({
                    value,
                    label: get_label(value)
                }));
            filterConfig = filterOptions(domainFilterOptions);
        }
        return {
            Header: /*#__PURE__*/ _jsx(ColHeader, {
                i18nKey: i18nKey
            }),
            accessor: (node)=>odid2ddata[node.odlink || node.odid][domprop],
            Cell: ({ value, aggregated })=>{
                if (aggregated) {
                    return mapItems(value, (k, v)=>/*#__PURE__*/ _jsx("span", {
                            children: get_label(k)
                        }, k));
                }
                return get_label(value);
            },
            aggregate: (vals)=>countBy(vals),
            needsColData: false,
            ...filterConfig,
            ...options
        };
    };
const DomainNameColumn = DomainPropertyColumn('name', 'col_dname');
const DomainTypeColumn = DomainPropertyColumn('typename', 'col_dtype', {
    isCategorial: true,
    translate: (v, i18n)=>i18n.get(`types_domain_${v}`)
});
const typesAggregated = (i18n, counted)=>mapItems(counted, (k, v)=>/*#__PURE__*/ _jsx("span", {
            style: {
                marginRight: '1px'
            },
            title: `${i18n.getType(k)} (${v})`,
            children: typeIcon(k)
        }, k));
const TypeColumn = (env, colid, options = {})=>{
    let f_names = options.fields?.map((f)=>col2field(f));
    // we have 3 sources for type names
    // all active types over descendant domains (activeDescendantTypes)
    // activated types in list fetcher (treeListOpts)
    // returned types from list fetcher (rowIndex)
    let { itypes, i18n, activeDescendantTypes, rowIndex = {
        typeNames: []
    }, treeListOpts } = env;
    activeDescendantTypes = activeDescendantTypes || {};
    // start with returned types
    let active_types = rowIndex.typeNames;
    if (treeListOpts.types) {
        // add types that have been used in api call
        // XXX: both forms?
        let list_fetch_types = isArray(treeListOpts.types) ? treeListOpts.types : treeListOpts.types.split(',');
        let datatypes = list_fetch_types.filter((t)=>t.startsWith('_'));
        let dt_types = flatten(datatypes.map((dt)=>activeDescendantTypes[dt.slice(1, -1)]).filter(Boolean));
        active_types = uniq([
            ...active_types,
            ...dt_types,
            ...list_fetch_types.filter((t)=>!t.startsWith('_'))
        ]);
    }
    // fallback to something
    if (active_types.length == 0) {
        active_types = activeDescendantTypes['metadata'] || [];
    }
    let typeFilterOptions = active_types.map((name)=>({
            value: String(name),
            label: i18n.getType(name)
        })) ?? [];
    //~ log.x(activeDescendantTypes, rowIndex.typeNames)
    //~ let typeFilterOptions = rowIndex.typeNames.map(name => ({
    //~ value: String(name),
    //~ label: i18n.getType(name),
    //~ }))
    let accessor = (node)=>itypes.byId[node.datatype][node.subtype];
    if (size(f_names)) {
        let [dt_field, st_field] = f_names;
        if (f_names.length == 1) {
            dt_field = null; // assume metadata (1)
            st_field = f_names[0];
        }
        // parent.rcv_datatype -> 'parent_rcv_datatype'
        dt_field = dt_field?.replace('.', '_');
        st_field = st_field?.replace('.', '_');
        accessor = (node)=>{
            if (!isNil(node[st_field])) {
                if (dt_field) {
                    return itypes.byId[node[dt_field]]?.[node[st_field]];
                } else {
                    // assume metadata
                    return itypes.byId[1][node[st_field]];
                }
            } else {
                return null;
            }
        };
    }
    let useTypeAnomaly = true;
    return {
        Header: /*#__PURE__*/ _jsx(ColHeader, {
            colid: colid
        }),
        accessor,
        Cell: ({ value, aggregated })=>{
            if (aggregated) {
                return typesAggregated(i18n, value);
            }
            return /*#__PURE__*/ _jsxs("span", {
                children: [
                    typeIcon(value),
                    " ",
                    i18n.getType(value)
                ]
            });
        },
        PivotValue: ({ value, subRows })=>{
            let label = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    typeIcon(value),
                    " ",
                    i18n.getType(value)
                ]
            });
            if (value === 'null') {
                label = 'Leer';
            }
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    label,
                    ` (${size(subRows)})`
                ]
            });
        },
        aggregate: (vals)=>countBy(vals),
        ...filterOptions(typeFilterOptions, useTypeAnomaly),
        needsColData: size(f_names) > 0,
        ...options
    };
};
const buildTypeCaption = (node, itypes, buildGo, i18n)=>{
    let tvalue = itypes.byId[node.datatype][node.subtype];
    return /*#__PURE__*/ _jsxs("span", {
        children: [
            typeIcon(tvalue, i18n.getType(tvalue)),
            ' ',
            /*#__PURE__*/ _jsx(Link, {
                lazy: true,
                request: buildGo(node),
                children: node.caption || i18n.getType(tvalue)
            })
        ]
    });
};
const TypeCaptionColumn = (env, colid, options)=>{
    let { itypes, buildGo, i18n } = env;
    return {
        id: colid,
        i18nKey: options.i18nKey,
        Header: /*#__PURE__*/ _jsx(ColHeader, {
            colid: "caption"
        }),
        accessor: (node)=>node.caption,
        Cell: ({ value, original, aggregated })=>{
            if (aggregated) {
                return typesAggregated(i18n, value);
            }
            return buildTypeCaption(original, itypes, buildGo, i18n);
        },
        ...filterMatchSorter('_original.caption'),
        needsColData: false,
        // rows can be undefined?
        aggregate: (vals, rows)=>rows ? countBy(rows.map((r)=>itypes.byId[r._original.datatype][r._original.subtype])) : {}
    };
};
const LinkColumn = (env, colid, options)=>({
        Header: /*#__PURE__*/ _jsx(ColHeader, {
            colid: colid
        }),
        Cell: ({ value, original })=>original ? /*#__PURE__*/ _jsx(Link, {
                lazy: true,
                request: env.buildGo(original),
                children: value || original.otid
            }) : null,
        aggregate: (vals)=>'',
        ...options,
        ...filterMatchSorter(colid)
    });
const MultiFieldColumn = (env, colid, options)=>{
    let f_names = options.fields.map((f)=>col2field(f));
    return {
        Header: /*#__PURE__*/ _jsx(ColHeader, {
            colid: colid,
            i18nKey: options.i18nKey
        }),
        accessor: (node)=>dict(f_names.map((f)=>[
                    f,
                    node[f]
                ])),
        Cell: ({ value })=>values(value).map((v)=>String(v)).join(', '),
        filterable: false,
        groupBySupport: false,
        aggregate: (vals)=>'',
        ...options
    };
};
export const ColumnTypes = {
    BooleanColumn,
    CustomBooleanColumn,
    TextColumn,
    NumberColumn,
    DateColumn,
    FileSizeColumn,
    MimetypeColumn,
    MimetypeTextColumn,
    MetadataRelationColumn,
    PercentageColumn,
    PageImageColumn,
    DomainStateColumn,
    StateColumn,
    UserColumn,
    IdColumn,
    DomainColumn,
    DomainNameColumn,
    DomainTypeColumn,
    TypeColumn,
    TypeCaptionColumn,
    LinkColumn,
    MultiFieldColumn,
    RelatedIdColumn,
    CategorialColumn,
    ResourceLinkColumn,
    ResourceColumn
};
export const ColumnTypeNames = dict(keys(ColumnTypes).map((e)=>[
        e,
        e
    ]));
export const registerColumnTypes = (coltypes)=>{
    items(coltypes).forEach(([name, coltype])=>{
        ColumnTypes[name] = coltype;
        ColumnTypeNames[name] = name;
    });
};
